import useHttpQuery from '@actions/services/firestore/browser/useHttpQuery';
import { SuperagentsResponse, UseSuperagentsInput, UseSuperagentsResult } from './types';

// types moved to ./types.ts

/**
 * Hook for fetching superagents from the API
 *
 * Fetches all available superagents for the authenticated user.
 * Uses useHttpQuery for efficient caching and error handling.
 * Authentication is automatically handled by useHttpQuery.
 *
 * @param options - Optional configuration for the hook
 * @param options.enabled - Whether the query should be enabled (default: true)
 * @returns Object containing superagents array, loading state, error state, and refetch function
 *
 * @example
 * ```tsx
 * const { superagents, isLoadingSuperagents, error } = useSuperagents({ enabled: true });
 *
 * if (isLoadingSuperagents) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 *
 * return (
 *   <div>
 *     {superagents.map(agent => (
 *       <div key={agent.id}>{agent.name}</div>
 *     ))}
 *   </div>
 * );
 * ```
 */
const useSuperagents = ({ enabled }: UseSuperagentsInput): UseSuperagentsResult => {
    const { data, isLoading, error, refetch } = useHttpQuery<SuperagentsResponse>({
        pathname: '/superagents',
        method: 'GET',
        key: 'superagents',
        queryOptions: { enabled },
    });

    return {
        superagents: data?.data || [],
        isLoadingSuperagents: isLoading,
        error: error || undefined,
        refetch,
    };
};

export default useSuperagents;
